import { useCallback, useEffect, useRef, useState } from "react";
import {
  FormTable,
  FormTableBody,
  FormTableColumnCell,
  FormTableColumnRow,
  FormTableItem,
  useApiCall,
} from "@porchpass/ui";
import {
  getDealerPortalV1DealerIdConfiguration,
  patchDealerPortalV1DealerIdConfiguration,
} from "@porchpass/api-client";
import { useDealer } from "src/hooks/useDealer";
import Toggle from "src/components/Toggle";
import {
  HeaderContainer,
  HeaderTitleContainer,
  HeaderTitle,
  HeaderSubTitle,
} from "../LendersTable/LendersTable.styles";
import TableHeader from "./TableHeader";

type PreferenceRow = {
  id: string;
  enabled: boolean;
};

const PREFERENCE_COPY = {
  title: "CC sales rep on application submits",
  description:
    "The sales rep assigned to an application is copied when an application is submitted to a lender. Turn off to submit without copying any dealership employees.",
};

export default () => {
  const { dealer_id } = useDealer();
  const [ccSalesRepOnLenderEmails, setCcSalesRepOnLenderEmails] =
    useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const previousValueRef = useRef(true);

  const loadConfiguration = useCallback(async () => {
    if (!dealer_id) throw new Error("No dealer ID");
    const response = await getDealerPortalV1DealerIdConfiguration(dealer_id);
    if (response.status !== 200) {
      throw new Error("Error fetching dealer configuration");
    }
    return response.data;
  }, [dealer_id]);

  const configurationQuery = useApiCall(loadConfiguration, {
    toast: {
      error: "Error fetching dealer configuration",
      useServerErrorMessage: true,
    },
    onSuccess: (data) => {
      setCcSalesRepOnLenderEmails(data.cc_sales_rep_on_lender_emails ?? true);
      setIsLoaded(true);
    },
    onError: (error) => {
      console.error("Error fetching dealer configuration:", error);
    },
  });

  const updateConfiguration = useCallback(
    async (enabled: boolean) => {
      if (!dealer_id) throw new Error("No dealer ID");
      await patchDealerPortalV1DealerIdConfiguration(dealer_id, {
        cc_sales_rep_on_lender_emails: enabled,
      });
      return enabled;
    },
    [dealer_id],
  );

  const configurationUpdate = useApiCall(updateConfiguration, {
    toast: {
      success: "Preference updated",
      error: "Error updating preference",
      useServerErrorMessage: true,
    },
    onError: (error) => {
      setCcSalesRepOnLenderEmails(previousValueRef.current);
      console.error("Error updating dealer configuration:", error);
    },
  });

  useEffect(() => {
    if (!dealer_id) return;
    void configurationQuery.execute();
  }, [dealer_id, configurationQuery.execute]);

  const updateCcSalesRepOnLenderEmails = (enabled: boolean) => {
    previousValueRef.current = ccSalesRepOnLenderEmails;
    setCcSalesRepOnLenderEmails(enabled);
    void configurationUpdate.execute(enabled);
  };

  const handleSortByStatus = () => {
    setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
  };

  const preferences: PreferenceRow[] = isLoaded
    ? [
        {
          id: "cc_sales_rep_on_lender_emails",
          enabled: ccSalesRepOnLenderEmails,
        },
      ]
    : [];

  const sortedPreferences = [...preferences].sort((a, b) => {
    if (a.enabled === b.enabled) return 0;
    return sortDirection === "asc" ? (a.enabled ? 1 : -1) : a.enabled ? -1 : 1;
  });

  return (
    <>
      <HeaderContainer>
        <HeaderTitleContainer>
          <HeaderTitle>Preferences</HeaderTitle>
          <HeaderSubTitle>
            Set how your dealership submits applications to lenders.
          </HeaderSubTitle>
        </HeaderTitleContainer>
      </HeaderContainer>

      <FormTable layout="columns" columns="88px minmax(0, 1fr)">
        <TableHeader
          onSortByStatus={handleSortByStatus}
          sortDirection={sortDirection}
        />
        {isLoaded && (
          <FormTableBody>
            {sortedPreferences.map((preference) => (
              <FormTableColumnRow key={preference.id}>
                <FormTableColumnCell>
                  <Toggle
                    selected={preference.enabled}
                    setSelected={updateCcSalesRepOnLenderEmails}
                  />
                </FormTableColumnCell>
                <FormTableColumnCell>
                  <FormTableItem
                    title={PREFERENCE_COPY.title}
                    description={PREFERENCE_COPY.description}
                  />
                </FormTableColumnCell>
              </FormTableColumnRow>
            ))}
          </FormTableBody>
        )}
      </FormTable>
    </>
  );
};
